/**
 * voice-v8 — die Karte aus dem Anzeige-Vertrag. (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 1)
 *
 * Martin: *„Ich möchte einen einfachen Auftrag, und dann brauche ich nicht hunderttausend Cards.“* Wer keine eigene
 * Karte hat, bekam bisher die generische: das rohe Ergebnis als Felder, Listen, „Rohdaten anzeigen“. Jetzt baut der
 * Server die Anzeige aus den Feldern (server/werkzeug-anzeige.ts), und hier wird sie NUR gerendert — mit den Bausteinen
 * des Grundgerüsts (lib/karte.ts), ohne eigenen Stil.
 *
 * ## Eine Karte je Familie und Zug
 *
 * Die Karte trägt den Kopf ihrer Familie („Blender“, „E-Mail“); jeder Aufruf ist EIN Eintrag darin: Titel, Stand oder
 * Zeile, darunter die Zeilen der Anzeige. Ein weiterer Aufruf derselben Familie im selben Zug hängt seinen Eintrag an die
 * vorhandene Karte (`haengeAnzeigeAn`) — keine zweite Karte, keine Faltung „· N-mal“. Offen stehen die letzten
 * ANZEIGE_EINTRAEGE_MAX, davor steht „und N weitere“.
 *
 * ## Ohne Anzeige
 *
 * Eine gespeicherte Karte aus der Zeit davor hat keine Anzeige. Hat ihr Werkzeug keine eigene Karte, wird sie eine leise
 * Zeile in den Schritten des Zuges („Name · erledigt“) — `leiseKarte` markiert sie, app.ts hängt die Zeile an. Nie ein
 * Dump.
 */
import { FAMILIEN_TITEL, istFamilie, kappeZeile, leseAnzeige, STAND_WORT, type Anzeige } from "../../shared/karten-anzeige.js";
import { baueKarte, liste, listenEintrag, weitereHinweis } from "./karte.js";
import { werkzeugName } from "./werkzeug-namen.js";

/** Die Art (Klasse `tool-anzeige`) — nur Kennung, kein eigener Stil. */
export const ANZEIGE_ART = "anzeige";

/** So lang wird die Zweitzeile eines Eintrags (die Zeilen der Anzeige, mit „ · “ verbunden) höchstens. */
const ZWEITZEILE_MAX = 240;

/** So viele Einträge zeigt eine Familienkarte offen — die letzten. */
export const ANZEIGE_EINTRAEGE_MAX = 5;

/** Der Kopf einer Familienkarte. */
export function familienTitel(a: Anzeige): string {
  return istFamilie(a.familie) && a.familie !== "sonst" ? FAMILIEN_TITEL[a.familie] : a.titel;
}

/**
 * So lang darf die Zeile rechts neben dem Titel sein. (15.10.2026, Welle 2c — in der Galerie gesehen)
 *
 * Die Nebenangabe des Grundgerüsts bricht nicht um (`.tool-list-meta`, nowrap): eine lange Zeile
 * („8.0.0-pre.3795 → 8.0.0-pre.3796 · 2 von 2 Schritten“) drückte den Titel auf „Vespe“ zusammen oder lief aus der
 * Karte. Länger als dies steht die Zeile darum als Zweitzeile unter dem Titel, rechts bleibt der Stand.
 */
const META_MAX = 32;

/** Der Titel eines Eintrags: ohne die Art, die der Kopf schon nennt („Mac · Kalender“ unter „Mac“ → „Kalender“). */
export function eintragsTitel(a: Anzeige): string {
  const kopf = familienTitel(a);
  const vorsatz = `${kopf} · `;
  return a.titel.startsWith(vorsatz) && a.titel.length > vorsatz.length ? a.titel.slice(vorsatz.length) : a.titel;
}

/** Ein Eintrag: Titel, rechts Zeile oder Stand, darunter die Zeilen der Anzeige. */
function eintrag(a: Anzeige): HTMLLIElement {
  const zeileKurz = a.zeile !== undefined && a.zeile.length <= META_MAX;
  // Steht die Zeile unten, sagt rechts nur ein Stand etwas, der nicht „ok“ ist — „erledigt“ neben einer Zahlenzeile wäre Lärm.
  const meta = a.stand === "fehler" ? STAND_WORT.fehler : zeileKurz ? a.zeile : a.zeile === undefined || a.stand !== "ok" ? STAND_WORT[a.stand] : "";
  // Beim Fehler steht der Grund als Zweitzeile — der Satz, nicht der Schlüssel (server/werkzeug-anzeige.ts).
  const unten = a.stand === "fehler" ? [a.zeile] : [zeileKurz ? undefined : a.zeile, ...(a.zeilen ?? [])];
  const zweitText = unten.filter((z): z is string => typeof z === "string" && z.length > 0).join(" · ");
  const zweit = zweitText ? kappeZeile(zweitText, ZWEITZEILE_MAX) : undefined;
  const li = listenEintrag(eintragsTitel(a), zweit, meta);
  li.dataset.stand = a.stand;
  return li;
}

/** Die Karte zu einer Anzeige — ein Eintrag; weitere hängt haengeAnzeigeAn an. */
export function baueAnzeigeKarte(a: Anzeige): HTMLDivElement {
  const el = baueKarte(ANZEIGE_ART, familienTitel(a));
  el.dataset.familie = a.familie;
  liste(el).appendChild(eintrag(a));
  return el;
}

/** Ist das eine Familienkarte aus einer Anzeige? */
export function istAnzeigeKarte(el: Element): el is HTMLElement {
  return el instanceof HTMLElement && el.classList.contains(`tool-${ANZEIGE_ART}`) && typeof el.dataset.familie === "string";
}

/** Der Schlüssel, unter dem die Karte im Behälter des Zuges hängt (`data-werkzeug`). */
export function familienSchluessel(familie: string): string {
  return `familie:${familie}`;
}

/** Nur die letzten ANZEIGE_EINTRAEGE_MAX Einträge, davor „und N weitere“. Bei jedem Anhängen neu gerechnet. */
function kuerze(karte: HTMLElement): void {
  const ul = karte.querySelector<HTMLElement>(":scope > .tool-list");
  if (!ul) return;
  const alle = Array.from(ul.children) as HTMLElement[];
  const zuViel = Math.max(0, alle.length - ANZEIGE_EINTRAEGE_MAX);
  alle.forEach((li, i) => {
    li.hidden = i < zuViel;
  });
  karte.querySelector(":scope > .tool-card-weitere")?.remove();
  const hinweis = weitereHinweis(karte, zuViel);
  if (hinweis) ul.before(hinweis);
}

/**
 * Eine Familienkarte in den Behälter ihres Zuges hängen — oder, wenn dort schon eine derselben Familie steht, ihren
 * Eintrag dort anhängen. Gibt die Karte zurück, die den Eintrag jetzt trägt.
 *
 * `einhaengen` ist die Regel des Behälters für eine NEUE Karte (app.ts: haengeKarteAn, vor der Uhrzeit).
 */
export function haengeAnzeigeAn(
  behaelter: HTMLElement,
  karte: HTMLElement,
  einhaengen: (behaelter: HTMLElement, karte: HTMLElement, schluessel: string) => void,
): HTMLElement {
  const familie = karte.dataset.familie ?? "sonst";
  const schon = Array.from(behaelter.querySelectorAll<HTMLElement>(`:scope > .tool-${ANZEIGE_ART}, :scope > .zug-recherche > .tool-${ANZEIGE_ART}`)).find(
    (k) => k !== karte && k.dataset.familie === familie,
  );
  if (!schon) {
    einhaengen(behaelter, karte, familienSchluessel(familie));
    return karte;
  }
  const ziel = schon.querySelector(":scope > .tool-list");
  const neu = karte.querySelectorAll(":scope > .tool-list > li");
  if (ziel) for (const li of Array.from(neu)) ziel.appendChild(li);
  kuerze(schon);
  return schon;
}

/** Die Markierung einer Karte, die keine sein soll: kein eigener Bau, keine Anzeige — app.ts macht eine leise Zeile daraus. */
export const LEISE = "ja";

/** Der Platzhalter für „keine eigene Karte, keine Anzeige“ — eine leise Zeile, nie ein Dump. */
export function leiseKarte(tool: string, isError: boolean): HTMLDivElement {
  const el = baueKarte("leise");
  el.dataset.leise = LEISE;
  liste(el).appendChild(listenEintrag(werkzeugName(tool), undefined, isError ? STAND_WORT.fehler : STAND_WORT.ok));
  return el;
}

/** Ist das der Platzhalter einer leisen Zeile? */
export function istLeiseKarte(el: Element): boolean {
  return el instanceof HTMLElement && el.dataset.leise === LEISE;
}

/** Die Anzeige einer Karte, geprüft und gekappt — sie kommt aus Ereignis, Verlauf oder Gerätespeicher. */
export function anzeigeVon(msg: { anzeige?: unknown }): Anzeige | null {
  return leseAnzeige(msg.anzeige);
}
